import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { verLeituras, type LeituraGravada } from '../api/adensamento';
import {
  Achado, Alca, Marco, Plano, Reta, Segmento, Serie, Zoom,
  br, casasDoPasso, maisPerto, passoRedondo, useJanela,
  type EixoPersonalizado, type Ponto,
} from '../Grafico';
import { Aviso, Carregando } from '../ui';
import { num } from './Adensamento';
import {
  ajusteCasagrande, ajusteTaylor, cruzamentoLog, ehTempoDeNorma, h0Casagrande,
  rotuloTempo, tEmAltura, type LeituraTH,
} from './adensamentoAjuste';

/**
 * Os pontos dos tempos de norma, destacados sobre a curva.
 *
 * «Deixe os pontos dos tempos de norma destacados no gráfico, como no
 *  gráfico da Suporte» (Humberto, 14/09/2026). A prensa lê a cada segundo
 *  e a curva vira uma fita; o técnico, porém, pensa nas leituras da NBR
 *  16853 (6 s, 15 s, 30 s, 1 min…), e é nelas que o olho procura.
 */
function PontosDeNorma({ leituras, px, x }: {
  leituras: LeituraTH[]; px(p: Ponto): Ponto; x(t: number): number;
}) {
  /*
   * Uma bolinha por tempo de norma — a leitura MAIS PRÓXIMA dele.
   *
   * A tolerância de 3 % casa várias leituras da prensa com o mesmo tempo
   * da norma (a 1 h ela aceita de 58 a 62 min). Ficar com a primeira
   * marcava 58 min e escrevia «1 h» no rótulo; a mais próxima marca o
   * que o rótulo diz.
   */
  const escolhidas = new Map<number, LeituraTH>();
  for (const l of leituras) {
    const tn = ehTempoDeNorma(l.t);
    if (tn == null) continue;
    const atual = escolhidas.get(tn);
    if (!atual || Math.abs(l.t - tn) < Math.abs(atual.t - tn)) escolhidas.set(tn, l);
  }
  return (
    <>
      {[...escolhidas].map(([tn, l]) => {
        const q = px({ x: x(l.t), y: l.h });
        if (!Number.isFinite(q.x) || !Number.isFinite(q.y)) return null;
        return (
          <g key={tn} className="ponto-norma">
            <circle cx={q.x} cy={q.y} r={4.5} />
            <title>{`${rotuloTempo(tn)} · ${num(l.h, 3)} mm`}</title>
          </g>
        );
      })}
    </>
  );
}

/**
 * O cruzamento da reta B com a curva vindo de BAIXO: o primeiro par de
 * leituras, com x ≥ xMin, em que a curva passa de abaixo da reta para
 * acima (ou sobre) dela. Ignora o cruzamento do transiente inicial, em
 * que a curva vem de cima.
 */
function cruzamento90(a: Ponto, b: Ponto, curva: Ponto[], xMin: number): Ponto | null {
  if (b.x === a.x) return null;
  const m = (b.y - a.y) / (b.x - a.x);
  const reta = (x: number) => a.y + m * (x - a.x);
  for (let i = 1; i < curva.length; i++) {
    const p = curva[i - 1], q = curva[i];
    if (q.x < xMin) continue;
    const dp = p.y - reta(p.x), dq = q.y - reta(q.x);
    if (dp < 0 && dq >= 0) {
      const f = dp / (dp - dq);
      return { x: p.x + f * (q.x - p.x), y: p.y + f * (q.y - p.y) };
    }
  }
  return null;
}

/** As leituras válidas de um estágio, em (t s, H mm), ordenadas. */
const emTH = (leituras: LeituraGravada[] | null): LeituraTH[] => (leituras ?? [])
  .filter((l) => !l.descartada && l.altura_mm != null && Number(l.tempo_s) > 0)
  .map((l) => ({ t: Number(l.tempo_s), h: Number(l.altura_mm) }))
  .sort((a, b) => a.t - b.t);

/**
 * As construções gráficas do adensamento — traçadas na tela.
 *
 * Humberto, 23/08/2026:
 *
 *   «os coeficientes de adensamento por Taylor e Casagrande devem ser
 *    calculados com auxílio de análise gráfica, conforme a planilha do
 *    ensaio. O de Taylor é mais fácil de programar e é o mais utilizado.
 *    Comece por ele (deixe o método de Casagrande in hold). Os índices de
 *    compressão, recompressão e expansão também devem ser determinados de
 *    forma gráfica. Depois vá para a determinação da tensão de
 *    pré-adensamento, que deverá ser feita também por método gráfico
 *    (mais utilizado é o método de Pacheco Silva). Todas as análises devem
 *    ser gráficas, feitas com a análise do usuário.»
 *
 * ─── O que mudou, e o que NÃO mudou ────────────────────────────────────
 *
 * Mudou o lugar do traçado: saiu do papel milimetrado e veio para a tela.
 * Não mudou de quem é o traçado. O sistema desenha a curva medida, obedece
 * à mão que arrasta a reta e refaz a aritmética da norma a partir de onde
 * a mão parou — nunca ajusta a reta sozinho (`D-27`). O nome de quem
 * traçou continua sendo gravado junto do resultado, como antes.
 *
 * Por isso não existe botão «calcular»: o valor aparece no instante em que
 * a reta se mexe, e o botão que existe é «gravar o que está traçado».
 *
 * ─── Casagrande fica em espera ─────────────────────────────────────────
 *
 * A pedido da Testesolo. O banco e a API já aceitam `casagrande` desde a
 * `0024` — quem preferir o log t continua podendo digitar H₁₀₀ e t₅₀ à
 * mão na tela de interpretação. O que não existe ainda é a construção
 * gráfica dele, e fingir que existe seria pior do que dizer que não.
 */


/*
 * ── Os eixos falam em minutos ─────────────────────────────────────────
 *
 * A curva continua em SEGUNDOS por dentro — é assim que o banco grava
 * `tempo_s`, `t90_s`, `t50_s` e é assim que o ajuste conta. O que muda é
 * o que se LÊ no eixo: √t em √min e t em minutos, como na planilha da
 * Suporte e no hábito de quem interpreta (Humberto, 14/09/2026: «corrija
 * os eixos dos gráficos de Cv — as unidades estão erradas»). Um eixo em
 * √s de 0 a 300 não diz nada a ninguém; 0 a 40 √min, sim.
 */
const RAIZ60 = Math.sqrt(60);

/** Eixo de Taylor: dado em √s, rótulo em √min, marcas redondas em √min. */
const EIXO_RAIZ_MIN: EixoPersonalizado = {
  marcas(lo, hi) {
    const r0 = lo / RAIZ60;
    const r1 = hi / RAIZ60;
    const passo = passoRedondo(Math.max((r1 - r0) / 6, 1e-6));
    const saida: number[] = [];
    for (let r = Math.ceil(r0 / passo) * passo; r <= r1 * 1.0001; r += passo) {
      saida.push(r * RAIZ60);
    }
    return saida.length ? saida : [lo, hi];
  },
  rotulo(v, todas) {
    const passo = todas.length > 1 ? (todas[1] - todas[0]) / RAIZ60 : 1;
    return br(v / RAIZ60, Math.min(2, casasDoPasso(Number(passo.toPrecision(6)))));
  },
};

/** Eixo de Casagrande: dado em s (log), rótulo nas DÉCADAS de minuto —
    0,1 · 1 · 10 · 100 · 1.000 — com as linhas de 2 e 5 sem número. */
const EIXO_LOG_MIN: EixoPersonalizado = {
  marcas(lo, hi) {
    const saida: number[] = [];
    for (let d = Math.floor(Math.log10(lo / 60)); d <= Math.ceil(Math.log10(hi / 60)); d += 1) {
      const v = 60 * 10 ** d;
      if (v >= lo * 0.999 && v <= hi * 1.001) saida.push(v);
    }
    return saida.length ? saida : [lo, hi];
  },
  menores(lo, hi) {
    const saida: number[] = [];
    for (let d = Math.floor(Math.log10(lo / 60)); d <= Math.ceil(Math.log10(hi / 60)); d += 1) {
      for (const m of [2, 5]) {
        const v = 60 * m * 10 ** d;
        if (v >= lo && v <= hi) saida.push(v);
      }
    }
    return saida;
  },
  rotulo(v) {
    const min = v / 60;
    return br(min, min < 1 ? Math.max(0, -Math.floor(Math.log10(min))) : 0);
  },
};

/** Um tempo em minutos, para o rótulo do achado (t₉₀ = 3,89 min). */
const emMin = (s: number) => `${num(s / 60, s / 60 < 10 ? 2 : 1)} min`;

/**
 * O «ⓘ» que guarda as instruções.
 *
 * «Remova todo aquele texto de explicação abaixo de cada gráfico, isso
 *  ocupa muito espaço. Crie um ícone de i onde é possível consultar as
 *  instruções passando o mouse por cima» — Humberto, 14/09/2026.
 *
 * Tem razão: o parágrafo explicava a mesma coisa duas vezes por tela, em
 * quatro linhas que empurravam os campos para baixo da dobra. A
 * instrução não desaparece — muda de estado: fica guardada e aparece
 * quando se procura por ela.
 *
 * Sem JavaScript. A dica abre no `:hover` e no `:focus-within` do
 * próprio ⓘ, por CSS. A classe é `dica-info` e não `dica`: `.dica` já é
 * a nota de rodapé de um campo (`ui.tsx`), e roubá-la mudaria toda nota
 * de campo do sistema. O `title` nativo não servia: não formata, demora,
 * some antes de se ler três linhas e não abre pelo teclado.
 */
export function Dica({ rotulo, children }: {
  rotulo: string; children: React.ReactNode;
}) {
  return (
    <span className="dica-info" tabIndex={0} aria-label={`Instruções — ${rotulo}`}>
      <span className="dica-info-i" aria-hidden="true">i</span>
      <span className="dica-balao" role="tooltip">{children}</span>
    </span>
  );
}

/** O que a mão pode fazer em qualquer plano — comum aos dois gráficos. */
function ComoMexer() {
  return (
    <>
      {' '}As alças <strong>grudam nas leituras</strong> — segure <kbd>Shift</kbd> para
      levá-las a qualquer lugar. A <strong>roda</strong> aproxima e afasta em volta do
      cursor (<kbd>Shift</kbd> + roda só na horizontal, <kbd>Alt</kbd> + roda só na
      vertical); o <strong>botão do meio</strong> arrasta o gráfico. Os pontos
      cheios são as leituras nos <strong>tempos da norma</strong> (7.3.2) — a prensa lê
      a cada segundo, e a curva passa por todas.
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 1 · Taylor (√t)
// ═══════════════════════════════════════════════════════════════════════

export interface TaylorTracado {
  h0Mm: number;
  h90Mm: number;
  t90S: number;
}

/**
 * A construção de Taylor, ponto a ponto.
 *
 * 1. A pessoa arrasta as duas alças até que a reta cubra o trecho inicial
 *    reto da curva (o adensamento primário em √t).
 * 2. Onde essa reta corta √t = 0 está **d₀** — o zero corrigido, que a
 *    norma chama de 0 % do adensamento primário. Não é a primeira leitura:
 *    o começo do estágio traz compressão de bolha e acomodação de topo, e
 *    é justamente isso que a extrapolação descarta.
 * 3. Uma segunda reta, com abscissa **1,15 vez** a da primeira para cada
 *    ordenada, corta a curva no ponto de **90 %** — daí (√t₉₀, d₉₀).
 * 4. t₉₀ = (√t₉₀)². O banco faz o resto: cv = 0,848 · H_d² / t₉₀.
 *
 * O 1,15 não é escolha nossa: é a razão entre as abscissas da teoria de
 * Terzaghi em 90 % e a tangente inicial. Está fixo no código porque mudá-lo
 * seria mudar o método, não a preferência.
 */
export function TaylorNoGrafico({
  ensaioId, estagioId, inicial, aoTracar, pode,
}: {
  ensaioId: string;
  estagioId: string;
  /** Valores já gravados, para reabrir o traçado onde ele parou. */
  inicial: TaylorTracado | null;
  aoTracar(v: TaylorTracado | null): void;
  pode: boolean;
}) {
  const [leituras, setLeituras] = useState<LeituraGravada[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [alcas, setAlcas] = useState<[Ponto, Ponto] | null>(null);
  /** Quais alças estão grudadas numa leitura de verdade (o ímã). */
  const [imantadas, setImantadas] = useState<boolean[]>([]);

  useEffect(() => {
    let vivo = true;
    setLeituras(null);
    setErro(null);
    setAlcas(null);
    setImantadas([]);
    verLeituras(ensaioId, estagioId)
      .then((l) => { if (vivo) setLeituras(l); })
      .catch(() => { if (vivo) setErro('Não consegui ler as leituras deste estágio.'); });
    return () => { vivo = false; };
  }, [ensaioId, estagioId]);

  /** A curva medida, em (√t, altura). Leitura descartada não entra. */
  const curva = useMemo<Ponto[]>(() => (leituras ?? [])
    .filter((l) => !l.descartada && l.altura_mm != null && Number(l.tempo_s) > 0)
    .map((l) => ({ x: Math.sqrt(Number(l.tempo_s)), y: Number(l.altura_mm) }))
    .sort((a, b) => a.x - b.x), [leituras]);

  /*
   * O enquadramento inicial mostra o ADENSAMENTO PRIMÁRIO, não o estágio
   * inteiro.
   *
   * Um estágio de 24 h chega a √t ≈ 300, e os 90 % costumam acontecer nos
   * primeiros minutos. Enquadrando tudo, a parte que interessa — o trecho
   * reto onde a reta é traçada — fica espremida nos primeiros milímetros
   * do eixo, e a pessoa começa toda sessão apertando «aproximar».
   *
   * O critério não é um palpite: procura-se a leitura em que o recalque já
   * cumpriu 95 % do recalque total do estágio, e enquadra-se até 1,6 vez o
   * √t dela. Sai dos dados, não de um número escolhido a dedo — e a
   * secundária continua a um clique de «reenquadrar».
   *
   * O zero entra sempre: é em √t = 0 que se lê o d₀, e um gráfico que
   * começa na primeira leitura esconde justamente a extrapolação.
   */
  const paraEnquadrar = useMemo<Ponto[]>(() => {
    if (!curva.length) return [];
    const comZero = [...curva, { x: 0, y: curva[0].y }];
    if (curva.length < 5) return comZero;
    const alto = curva[0].y;
    const baixo = curva[curva.length - 1].y;
    const alvo = alto - 0.95 * (alto - baixo);
    const i = curva.findIndex((p) => p.y <= alvo);
    if (i <= 1) return comZero;
    const xLimite = curva[i].x * 1.6;
    const dentro = comZero.filter((p) => p.x <= xLimite);
    return dentro.length >= 4 ? dentro : comZero;
  }, [curva]);

  const z = useJanela(paraEnquadrar, 0.06, { x: 'raiz' });

  /*
   * Onde as alças começam.
   *
   * No trecho que quase sempre É o primário: do segundo ponto medido até
   * cerca de 40 % da curva. É um ponto de partida, não um palpite com
   * ares de resultado — a pessoa vai arrastar as duas de qualquer jeito,
   * e o que fica gravado é onde ELA parou.
   *
   * Quando o estágio já tem traçado gravado, as alças voltam para ele: a
   * reta é reconstruível a partir de d₀, d₉₀ e t₉₀ sem guardar mais nada.
   */
  const th = useMemo(() => emTH(leituras), [leituras]);
  const [automatico, setAutomatico] = useState<boolean | null>(null);

  /**
   * O ajuste automático (14/09/2026): a reta inicial por mínimos quadrados
   * no trecho de 10 % a 60 % do recalque (ver `adensamentoAjuste`). É o
   * ponto de partida; as alças continuam arrastáveis, e o que se grava é
   * onde elas pararem. Sem trecho para ajustar, cai no chute antigo — e a
   * tela diz que é chute.
   */
  const tracarAutomatico = () => {
    const ajuste = ajusteTaylor(th);
    if (ajuste) { setAlcas(ajuste); setAutomatico(true); return; }
    const i1 = Math.min(1, curva.length - 1);
    const i2 = Math.max(i1 + 1, Math.floor(curva.length * 0.4));
    setAlcas([curva[i1], curva[Math.min(i2, curva.length - 1)]]);
    setAutomatico(false);
  };

  useEffect(() => {
    if (!curva.length || alcas) return;
    if (inicial && inicial.t90S > 0) {
      // Reta B passa por (0, d₀) e (√t₉₀, d₉₀); a reta A tem 1/1,15 da
      // abscissa de B, então basta encolher o x.
      const xb = Math.sqrt(inicial.t90S);
      setAlcas([
        { x: 0, y: inicial.h0Mm },
        { x: xb / 1.15, y: inicial.h90Mm },
      ]);
      setAutomatico(null);
      return;
    }
    tracarAutomatico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curva, inicial, alcas]);

  /** d₀, d₉₀ e t₉₀ a partir de onde as alças estão agora. */
  const achado = useMemo(() => {
    if (!alcas || curva.length < 3) return null;
    const [a, b] = alcas;
    if (a.x === b.x) return null;
    const inclinacao = (b.y - a.y) / (b.x - a.x);
    const d0 = a.y - inclinacao * a.x;          // reta A em √t = 0
    // Reta B: mesmo d₀, abscissa 1,15 vez maior para a mesma ordenada.
    const bB: Ponto = { x: 1.15 * (b.x - a.x) + 1.15 * a.x, y: b.y };
    /*
     * O cruzamento certo é o de BAIXO (Humberto, 14/09/2026: «o t₉₀ está
     * pegando na parte superior da reta 1,15, quando deveria pegar na
     * parte inferior, no intercepto da curva»). A curva da prensa começa
     * ACIMA de d₀ (transiente de aplicação de carga) e desce cruzando a
     * reta B uma primeira vez — esse cruzamento é o transiente, não os
     * 90 %. Os 90 % são onde a curva, já abaixo da reta, volta a subir
     * sobre ela ao achatar. Procura-se então, a partir da primeira alça,
     * a passagem de «curva abaixo da reta» para «curva acima».
     */
    const p90 = cruzamento90({ x: 0, y: d0 }, bB, curva, Math.min(a.x, b.x));
    if (!p90 || p90.x <= 0) return { d0, retaB: bB, p90: null };
    return { d0, retaB: bB, p90, t90: p90.x * p90.x };
  }, [alcas, curva]);

  /* O resultado sobe para a tela de interpretação a cada arrasto: os
     campos H₀/H₉₀/t₉₀ continuam existindo e continuam editáveis, e o
     gráfico é mais um jeito de preenchê-los — não um jeito paralelo. */
  useEffect(() => {
    if (!achado?.p90 || achado.t90 == null) { aoTracar(null); return; }
    aoTracar({ h0Mm: achado.d0, h90Mm: achado.p90.y, t90S: achado.t90 });
    // `aoTracar` muda de identidade a cada render do pai; depender dela
    // aqui faria o efeito rodar em laço.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achado]);

  if (erro) return <Aviso tipo="erro">{erro}</Aviso>;
  if (!leituras) return <Carregando texto="Abrindo as leituras do estágio" />;
  if (curva.length < 3) {
    return (
      <p className="nada">
        Este estágio tem {curva.length} leitura(s) válida(s). Taylor precisa
        do trecho inicial inteiro para a reta significar alguma coisa —
        lance as leituras na bancada antes de traçar.
      </p>
    );
  }

  return (
    <div className="grafico taylor">
      <div className="topo-grafico">
        <Zoom z={z} />
        <Dica rotulo="o traçado de Taylor">
          Arraste as duas alças até a reta cobrir o <strong>trecho reto
          inicial</strong> da curva. A reta clara é a de 1,15 × abscissa: onde
          ela corta a curva está o <strong>90 % do primário</strong>, e a
          abscissa desse ponto é t₉₀. O d₀ é onde a reta das alças encontra
          √t = 0.<ComoMexer />
        </Dica>
      </div>
      <Plano
        janela={z.janela}
        tituloX="Raiz do tempo — √t (√min)"
        tituloY="Altura do corpo de prova — H (mm)"
        eixoX={EIXO_RAIZ_MIN}
        alcas={alcas ?? []}
        ima={curva}
        aoJanela={z.definir}
        desabilitado={!pode}
        aoArrastar={(p, i, imantado) => {
          setImantadas((antes) => { const n = [...antes]; n[i] = imantado; return n; });
          setAlcas((antes) => {
            if (!antes) return antes;
            const novo: [Ponto, Ponto] = [antes[0], antes[1]];
            novo[i] = p;
            return novo;
          });
        }}
      >
        {({ px }) => (
          <>
            {/* A curva sem bolinha: as leituras intermediárias são a fita
                da prensa (uma por segundo) e poluíam o gráfico — ficam só
                os tempos da norma (Humberto, 14/09/2026). */}
            <Serie pontos={curva} px={px} marcar={false} />
            <PontosDeNorma leituras={th} px={px} x={Math.sqrt} />
            {alcas && <Reta a={alcas[0]} b={alcas[1]} px={px} classe="reta" />}
            {achado && (
              <Reta a={{ x: 0, y: achado.d0 }} b={achado.retaB} px={px}
                classe="reta noventa" />
            )}
            {achado && (
              <Achado p={{ x: 0, y: achado.d0 }} px={px}
                rotulo={`d₀ = ${num(achado.d0, 4)} mm`} />
            )}
            {achado?.p90 && (
              <Achado p={achado.p90} px={px}
                rotulo={`90 % · t₉₀ = ${emMin(achado.t90)}`} />
            )}
            {alcas && <Alca p={alcas[0]} px={px} rotulo="1" imantada={imantadas[0]} />}
            {alcas && <Alca p={alcas[1]} px={px} rotulo="2" imantada={imantadas[1]} />}
          </>
        )}
      </Plano>

      <div className="rodape-grafico">
        {pode && (
          <button type="button" className="ligado" onClick={tracarAutomatico}>
            Traçar de novo automaticamente
          </button>
        )}
        <span className="estado-tracado">
          {!pode ? 'Você está vendo, não traçando.'
            : automatico === true ? 'Traçado automático sobre o trecho reto (10 % a 60 % do recalque).'
              : automatico === false ? 'Sem trecho reto para ajustar — as alças estão num chute.'
                : inicial ? 'As alças estão onde o traçado gravado parou.' : ''}
        </span>
      </div>

      {achado && !achado.p90 && (
        <Aviso tipo="atencao">
          A reta de 90 % não cruza a curva dentro do que foi medido. Ou a
          reta inicial ainda não está sobre o trecho reto, ou o estágio foi
          interrompido antes dos 90 % — e nesse caso t₉₀ não existe para
          ser lido.
        </Aviso>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 1b · Casagrande (log t)
// ═══════════════════════════════════════════════════════════════════════

export interface CasagrandeTracado {
  h0Mm: number; h100Mm: number; t50S: number;
  /** Só quando o H₀ veio da parábola: com o H₀ movido à mão não há t₁. */
  t1S?: number; t2S?: number;
}

/**
 * A construção de Casagrande sobre a curva H × log t (NBR 16853, 7.4.2).
 *
 * 1. **H₀** pelo método da parábola (7.4.2.3): escolhe-se t₁ (um tempo de
 *    norma) e t₂ = 4 t₁; a diferença de altura entre os dois, marcada
 *    acima da leitura de t₁, é o 0 % do primário.
 * 2. **H₁₀₀** é o cruzamento da tangente ao ponto de inflexão com a reta
 *    do adensamento secundário (7.4.2.2) — as duas retas são as quatro
 *    alças deste gráfico.
 * 3. **H₅₀** = (H₀ + H₁₀₀)/2 (7.4.2.4) e **t₅₀** é onde a curva passa por
 *    H₅₀. O banco faz o resto: cv = 0,197 · H_d² / t₅₀.
 *
 * As retas nascem do ajuste automático (`ajusteCasagrande`) e a pessoa
 * arrasta o que quiser — o que se grava é onde as alças pararem
 * (Humberto, 14/09/2026: «trace automaticamente […] caso o usuário
 * queira, ele pode ajustar como é hoje»).
 */
export function CasagrandeNoGrafico({
  ensaioId, estagioId, inicial, aoTracar, pode,
}: {
  ensaioId: string;
  estagioId: string;
  inicial: CasagrandeTracado | null;
  aoTracar(v: CasagrandeTracado | null): void;
  pode: boolean;
}) {
  const [leituras, setLeituras] = useState<LeituraGravada[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Quatro alças: [tangente a, tangente b, secundária a, secundária b], x = t em s. */
  const [alcas, setAlcas] = useState<[Ponto, Ponto, Ponto, Ponto] | null>(null);
  /** Quais alças estão grudadas numa leitura de verdade (o ímã). */
  const [imantadas, setImantadas] = useState<boolean[]>([]);
  const [t1, setT1] = useState<number | null>(null);
  /**
   * O H₀ movido à mão (Humberto, 14/09/2026: «no método de Casagrande,
   * possibilite ajustar manualmente a altura H₀, que fica fixa apenas em
   * alguns pontos específicos»). A parábola (7.4.2.3) só sabe propor o H₀
   * a partir de um t₁ da norma — quatro alturas possíveis, e nenhuma
   * delas obrigatoriamente a certa. Com o H₀ à mão a construção deixa de
   * ter t₁/t₂ e o laudo não finge que teve.
   */
  const [h0Mao, setH0Mao] = useState<number | null>(null);
  const [automatico, setAutomatico] = useState<boolean | null>(null);

  useEffect(() => {
    let vivo = true;
    setLeituras(null); setErro(null); setAlcas(null); setImantadas([]);
    setT1(null); setH0Mao(null);
    verLeituras(ensaioId, estagioId)
      .then((l) => { if (vivo) setLeituras(l); })
      .catch(() => { if (vivo) setErro('Não consegui ler as leituras deste estágio.'); });
    return () => { vivo = false; };
  }, [ensaioId, estagioId]);

  const th = useMemo(() => emTH(leituras), [leituras]);
  const curva = useMemo<Ponto[]>(() => th.map((l) => ({ x: l.t, y: l.h })), [th]);

  const tracarAutomatico = (t1Pedido?: number) => {
    setH0Mao(null);
    const aj = ajusteCasagrande(th, t1Pedido);
    if (aj) {
      setAlcas([aj.tangente[0], aj.tangente[1], aj.secundaria[0], aj.secundaria[1]]);
      setT1(aj.t1);
      setAutomatico(true);
      return;
    }
    if (curva.length >= 4) {
      const n = curva.length;
      setAlcas([curva[Math.floor(n * 0.3)], curva[Math.floor(n * 0.5)],
        curva[n - 3], curva[n - 1]]);
      setT1(t1Pedido ?? 15);
      setAutomatico(false);
    }
  };

  useEffect(() => {
    if (!curva.length || alcas) return;
    if (inicial && inicial.t50S > 0) {
      /*
       * Reabrir onde parou: H₀ e t₁/t₂ vêm gravados; a tangente e a
       * secundária não são gravadas (o que se grava é H₁₀₀, o cruzamento),
       * então as retas voltam do ajuste automático e a secundária é
       * puxada para passar em H₁₀₀ — o cruzamento reproduz o gravado.
       */
      const aj = ajusteCasagrande(th, inicial.t1S || undefined);
      if (aj) {
        const c = cruzamentoLog(aj.tangente, aj.secundaria);
        const dh = c ? inicial.h100Mm - c.h : 0;
        setAlcas([aj.tangente[0], aj.tangente[1],
          { ...aj.secundaria[0], y: aj.secundaria[0].y + dh },
          { ...aj.secundaria[1], y: aj.secundaria[1].y + dh }]);
        setT1(inicial.t1S || aj.t1);
        setAutomatico(null);
        return;
      }
    }
    tracarAutomatico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curva, inicial, alcas]);

  /**
   * H₀ (da parábola ou da mão); H₁₀₀ pelo cruzamento; H₅₀ e t₅₀ da curva.
   *
   * `parabola` é `null` quando o H₀ foi movido à mão — é o que faz o
   * laudo gravar t₁/t₂ vazios em vez de herdar os da proposta.
   */
  const achado = useMemo(() => {
    if (!alcas || th.length < 4) return null;
    const parabola = h0Mao == null && t1 ? h0Casagrande(th, t1) : null;
    const h0 = h0Mao ?? parabola?.h0 ?? null;
    const c = cruzamentoLog([alcas[0], alcas[1]], [alcas[2], alcas[3]]);
    if (h0 == null || !c) {
      return { h0, parabola, c, h50: null as number | null, t50: null as number | null };
    }
    const h50 = (h0 + c.h) / 2;
    const t50 = tEmAltura(th, h50);
    return { h0, parabola, c, h50, t50 };
  }, [alcas, t1, th, h0Mao]);

  useEffect(() => {
    if (achado?.h0 == null || !achado.c || achado.t50 == null || achado.t50 <= 0) {
      aoTracar(null); return;
    }
    aoTracar({ h0Mm: achado.h0, h100Mm: achado.c.h, t50S: achado.t50,
      t1S: achado.parabola?.t1, t2S: achado.parabola?.t2 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achado]);

  const h0Y = achado?.h0 ?? undefined;
  const z = useJanela(useMemo(() => {
    if (!curva.length) return [];
    const extra: Ponto[] = [];
    if (h0Y != null) extra.push({ x: curva[0].x, y: h0Y });
    return [...curva, ...extra];
  }, [curva, h0Y]), 0.08, { x: 'log' });

  if (erro) return <Aviso tipo="erro">{erro}</Aviso>;
  if (!leituras) return <Carregando texto="Abrindo as leituras do estágio" />;
  if (curva.length < 4) {
    return (
      <p className="nada">
        Este estágio tem {curva.length} leitura(s) válida(s). Casagrande
        precisa da curva inteira — do transiente à secundária — para as
        duas retas significarem alguma coisa.
      </p>
    );
  }

  const opcoesT1 = [6, 15, 30, 60].filter((t) => th.some((l) => l.t >= 4 * t));
  const hEmT = (t: number) => th.find((l) => l.t >= t)?.h ?? th[th.length - 1].h;
  /* O H₀ é lido no começo da curva: a alça fica sobre t₁ (onde a parábola
     o marca) e só se move na vertical — arrastá-la no tempo não quer
     dizer nada. */
  const tH0 = achado?.parabola?.t1 ?? t1 ?? 15;
  const alcasDoPlano: Ponto[] = alcas
    ? (achado?.h0 != null ? [...alcas, { x: tH0, y: achado.h0 }] : [...alcas])
    : [];

  return (
    <div className="grafico casagrande">
      <div className="topo-grafico">
        <Zoom z={z} />
        <Dica rotulo="o traçado de Casagrande">
          As alças <strong>1 e 2</strong> são a tangente ao ponto de inflexão;
          as <strong>3 e 4</strong>, a reta do adensamento secundário. Onde as
          duas se cruzam está <strong>H₁₀₀</strong>. O <strong>H₀</strong> é a
          quinta alça: o sistema propõe pela parábola (7.4.2.3, com t₁ e
          t₂ = 4 t₁) e você arrasta se a proposta não servir. H₅₀ é a média
          dos dois, e t₅₀ é onde a curva passa por ela.<ComoMexer />
        </Dica>
      </div>
      <Plano
        janela={z.janela}
        escalaX="log"
        tituloX="Tempo — t (min), escala logarítmica"
        tituloY="Altura do corpo de prova — H (mm)"
        eixoX={EIXO_LOG_MIN}
        /* A quinta alça é o H₀: entra no plano, não no estado das retas. */
        alcas={alcasDoPlano}
        ima={curva}
        alcasSemIma={[4]}
        aoJanela={z.definir}
        desabilitado={!pode}
        aoArrastar={(p, i, imantado) => {
          setImantadas((antes) => { const n = [...antes]; n[i] = imantado; return n; });
          if (i === 4) { setH0Mao(p.y); return; }
          setAlcas((antes) => {
            if (!antes) return antes;
            const novo: [Ponto, Ponto, Ponto, Ponto] = [antes[0], antes[1], antes[2], antes[3]];
            novo[i] = { x: Math.max(p.x, 1e-3), y: p.y };
            return novo;
          });
        }}
      >
        {({ px }) => (
          <>
            <Serie pontos={curva} px={px} marcar={false} />
            <PontosDeNorma leituras={th} px={px} x={(t) => t} />
            {alcas && <Reta a={alcas[0]} b={alcas[1]} px={px} classe="reta" />}
            {alcas && <Reta a={alcas[2]} b={alcas[3]} px={px} classe="reta noventa" />}
            {achado?.h0 != null && (
              <Reta a={{ x: 1, y: achado.h0 }} b={{ x: 10, y: achado.h0 }} px={px}
                classe="reta auxiliar" />
            )}
            {achado?.c && (
              <Reta a={{ x: 1, y: achado.c.h }} b={{ x: 10, y: achado.c.h }} px={px}
                classe="reta auxiliar" />
            )}
            {achado?.parabola && (
              <>
                <Marco p={{ x: achado.parabola.t1, y: hEmT(achado.parabola.t1) }} px={px} rotulo="t₁" />
                <Marco p={{ x: achado.parabola.t2, y: hEmT(achado.parabola.t2) }} px={px} rotulo="t₂ = 4 t₁" />
              </>
            )}
            {achado?.c && (
              <Achado p={{ x: achado.c.t, y: achado.c.h }} px={px}
                rotulo={`H₁₀₀ = ${num(achado.c.h, 4)} mm`} />
            )}
            {achado?.t50 != null && achado.h50 != null && (
              <Achado p={{ x: achado.t50, y: achado.h50 }} px={px}
                rotulo={`50 % · t₅₀ = ${emMin(achado.t50)}`} />
            )}
            {alcas && <Alca p={alcas[0]} px={px} rotulo="1" imantada={imantadas[0]} />}
            {alcas && <Alca p={alcas[1]} px={px} rotulo="2" imantada={imantadas[1]} />}
            {alcas && <Alca p={alcas[2]} px={px} rotulo="3" imantada={imantadas[2]} />}
            {alcas && <Alca p={alcas[3]} px={px} rotulo="4" imantada={imantadas[3]} />}
            {/* O H₀ é alça E resultado: um marcador só, com o valor no
                rótulo — dois círculos no mesmo ponto seriam ruído. */}
            {achado?.h0 != null && (
              <Alca p={{ x: tH0, y: achado.h0 }} px={px}
                rotulo={`H₀ = ${num(achado.h0, 4)} mm${h0Mao != null ? ' (à mão)' : ''}`} />
            )}
          </>
        )}
      </Plano>

      <div className="rodape-grafico">
        {pode && (
          <button type="button" className="ligado"
            onClick={() => tracarAutomatico(t1 ?? undefined)}>
            Traçar de novo automaticamente
          </button>
        )}
        {pode && h0Mao != null && (
          <button type="button" className="discreto"
            onClick={() => setH0Mao(null)}>
            H₀ pela parábola
          </button>
        )}
        {pode && (
          <label className="t1-parabola">
            t₁{' '}
            <select value={t1 ?? ''} onChange={(e) => {
              const v = Number(e.currentTarget.value);
              setT1(v); setH0Mao(null);
            }} aria-label="t₁ do método da parábola">
              {opcoesT1.map((t) => <option key={t} value={t}>{rotuloTempo(t)}</option>)}
            </select>
          </label>
        )}
        <span className="estado-tracado">
          {!pode ? 'Você está vendo, não traçando.'
            : h0Mao != null ? 'H₀ movido à mão — sem parábola, sem t₁.'
              : automatico === true ? 'Traçado automático: tangente na inflexão e secundária na última década.'
                : automatico === false ? 'Sem inflexão nem secundária para ajustar — as alças estão num chute.'
                  : inicial ? 'As retas reproduzem o H₁₀₀ gravado.' : ''}
        </span>
      </div>

      {achado && (!achado.c || achado.t50 == null) && (
        <Aviso tipo="atencao">
          {!achado.c
            ? 'As duas retas não se cruzam — arraste as alças até a tangente e a secundária se encontrarem.'
            : 'A curva não passa por H₅₀ dentro do que foi medido — ou H₀/H₁₀₀ estão fora da curva, ou o estágio foi interrompido antes.'}
        </Aviso>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 2 · A curva de compressibilidade: Cc, Cr, Cs e σ'p
// ═══════════════════════════════════════════════════════════════════════

export const TRECHOS = [
  { chave: 'recompressao', nome: 'Cr — recompressão', classe: 'cr' },
  { chave: 'compressao', nome: 'Cc — compressão virgem', classe: 'cc' },
  { chave: 'descompressao', nome: 'Cs — descompressão', classe: 'cs' },
] as const;

export type Trecho = typeof TRECHOS[number]['chave'];
export type Selecao = Record<string, { a?: string; b?: string }>;

interface PontoEstagio extends Ponto { id: string; ordem: number; fase: string }

/**
 * A curva e × log σ′, com os três trechos e a construção de Pacheco Silva.
 *
 * ─── Como se usa ───────────────────────────────────────────────────────
 *
 * Escolhe-se o trecho e clica-se em dois pontos da curva. O sistema traça
 * a reta e calcula o índice — Cc, Cr ou Cs — pela definição de 7.5.2:
 *
 *     C = (e₁ − e₂) / (log σ′₂ − log σ′₁)
 *
 * Dois pontos e não um ajuste por mínimos quadrados: a norma define o
 * índice por dois pontos DO TRECHO, e qual trecho é reto é julgamento de
 * quem lê a curva. Um ajuste automático decidiria isso no lugar da pessoa
 * e esconderia a decisão dentro de uma média.
 *
 * ─── Pacheco Silva ─────────────────────────────────────────────────────
 *
 * Assim que o trecho virgem existe, a construção sai sozinha — porque ela
 * é inteiramente determinada pela reta virgem e por e₀, sem nenhuma
 * escolha adicional:
 *
 *   A. horizontal em e₀ até encontrar o prolongamento da reta virgem;
 *   B. daí, vertical até a curva de laboratório;
 *   C. daí, horizontal até a reta virgem outra vez;
 *   D. a abscissa desse último ponto é σ′p.
 *
 * É por isso que ela aparece desenhada sem pedir mais nada: o julgamento
 * já foi dado quando a pessoa escolheu onde está o trecho virgem.
 */
/** Uma lacuna que impede a curva, como o banco a enumera (`0103`). */
export interface Falta {
  falta: string;
  onde: string | null;
  grandeza: string | null;
  certificado: string | null;
  certificado_id: string | null;
  certificado_status: string | null;
  certificado_titulo: string | null;
}

const NOME_DA_FALTA: Record<string, string> = {
  altura_do_anel: 'a altura do anel',
  volume_do_anel: 'o volume do anel',
  tara_do_anel: 'a tara do anel',
  massa_especifica_dos_graos: 'a massa específica dos grãos',
  umidade_inicial: 'a umidade inicial',
  estagios_com_indice: 'três estágios com índice de vazios',
};

/**
 * Por que a curva não está aqui.
 *
 * A resposta antiga — «aparece assim que a bancada terminar» — era certa
 * numa hipótese só. Nas outras ela mandava a pessoa esperar por algo que
 * já tinha acontecido. O banco sabe o anel, a grandeza e o número do
 * certificado onde a constante já está medida; esconder isso e pedir
 * paciência é a pior combinação possível.
 */
function SemCurva({ faltas }: { faltas?: Falta[] }) {
  if (!faltas || faltas.length === 0) {
    return (
      <p className="nada">
        A curva precisa de pelo menos três estágios com índice de vazios
        calculado. Ela aparece aqui assim que a bancada terminar.
      </p>
    );
  }

  /* A constante que já existe medida vem primeiro: é a que se resolve
     com um clique, e não com uma nova pesagem. */
  const comCertificado = faltas.filter((f) => f.certificado);
  const semCertificado = faltas.filter((f) => !f.certificado);

  return (
    <Aviso tipo="atencao" titulo="A curva não sai porque falta uma constante.">
      {comCertificado.length > 0 && (
        <>
          <p>
            {comCertificado.map((f, i) => (
              <span key={f.falta}>
                {i > 0 && ', '}
                <b>{NOME_DA_FALTA[f.falta] ?? f.falta}</b>
                {f.onde && <> ({f.onde})</>}
              </span>
            ))}
            {comCertificado.length === 1 ? ' não está publicada' : ' não estão publicadas'}
            {' '}— o valor já foi calibrado e está no certificado{' '}
            {[...new Set(comCertificado.map((f) => f.certificado))].map((c, i) => (
              <span key={c}>{i > 0 && ', '}<b>{c}</b></span>
            ))}
            {comCertificado[0].certificado_status === 'rascunho'
              ? ', que ainda está em rascunho. Aprovar o certificado publica a '
                + 'constante, e a curva aparece sozinha.'
              : '.'}
          </p>
          <p>
            <Link to="/equipamentos/verificacoes">
              Ir para os certificados internos
            </Link>
          </p>
        </>
      )}

      {semCertificado.length > 0 && (
        <p>
          Falta ainda{' '}
          {semCertificado.map((f, i) => (
            <span key={f.falta}>
              {i > 0 && ', '}
              <b>{NOME_DA_FALTA[f.falta] ?? f.falta}</b>
            </span>
          ))}
          {'. '}
          {semCertificado.some((f) => f.falta === 'estagios_com_indice')
            ? 'Os estágios precisam de leitura lançada — isso é da bancada.'
            : 'Isso entra no cabeçalho do ensaio.'}
        </p>
      )}
    </Aviso>
  );
}

export function CurvaNoGrafico({
  estagios, selecao, trechoAtivo, aoClicarPonto, e0, preAdensamento, pode,
  aoSugerirSigma, mostrarPacheco = true, marca = 'indices', faltas,
}: {
  estagios: any[];
  selecao: Selecao;
  trechoAtivo: Trecho;
  aoClicarPonto(estagioId: string): void;
  e0: number | null;
  /** σ′p já gravada, para desenhar junto do que a construção sugere. */
  preAdensamento: number | null;
  pode: boolean;
  /** Sobe a σ′p da construção para o bloco que a assina. */
  aoSugerirSigma(v: number | null): void;
  /*
   * Desenhar ou não a construção de Pacheco Silva.
   *
   * «Acredito que seja melhor fazer os gráficos dos índices Cc, Cs e Cr
   *  separado do gráfico de tensão de pré adensamento. Fica melhor a
   *  visualização.» — e é verdade: sobre a mesma curva, as três retas de
   *  índice e as três linhas da construção viram seis traços, e a pessoa
   *  perde de vista qual reta está ajustando. O mesmo componente serve
   *  aos dois blocos; o que muda é o que ele mostra.
   */
  mostrarPacheco?: boolean;
  /*
   * Um nome para o gráfico, virado classe no invólucro.
   *
   * Desde que a curva aparece DUAS vezes na mesma tela — uma com as retas
   * de índice, outra com a construção —, "o último plano da página" deixou
   * de identificar coisa nenhuma. Quem precisa apontar para um deles
   * (teste, folha de estilo, quem for) aponta pelo nome.
   */
  marca?: string;
  /*
   * O que impede a curva, vindo do banco (`0103`).
   *
   * Sem isto a tela dizia «aparece assim que a bancada terminar» — e
   * mandava procurar no lugar errado quando a bancada já tinha
   * terminado e o que faltava era a constante do anel, medida e à
   * espera de uma assinatura noutra tela.
   */
  faltas?: Falta[];
}) {
  const pontos = useMemo<PontoEstagio[]>(() => estagios
    .filter((s) => s.indice_vazios != null && Number(s.pressao_kpa) > 0)
    .map((s) => ({
      id: s.id,
      ordem: s.ordem,
      fase: s.fase,
      x: Number(s.pressao_kpa),
      y: Number(s.indice_vazios),
    }))
    .sort((a, b) => a.ordem - b.ordem), [estagios]);

  /*
   * Carregamento e descarregamento são duas poligonais — e elas se TOCAM.
   *
   * «Não está conectando o gráfico do último ponto de carregamento com o
   *  último ponto de descarregamento» — Humberto, 01/09/2026.
   *
   * São duas poligonais porque o alívio volta por cima da ida em escala
   * log e uma linha só desenharia um laço que não é a curva de ninguém.
   * Mas separá-las por completo criou o defeito oposto: o ramo de volta
   * aparecia solto no gráfico, começando no ar, como se o corpo de prova
   * tivesse pulado de 400 kPa para 200 sem passar pela descarga.
   *
   * O ponto de virada é UM só e pertence às duas: é o último
   * carregamento, e é dele que o alívio parte. Repeti-lo no começo da
   * segunda poligonal é o que o laudo já fazia
   * (`adensamento-documento`, `pts.slice(nCarga - 1)`) — a tela é que
   * estava divergindo do papel.
   */
  const carga = pontos.filter((p) => p.fase !== 'descarregamento');
  const soDescarga = pontos.filter((p) => p.fase === 'descarregamento');
  const descarga = soDescarga.length && carga.length
    ? [carga[carga.length - 1], ...soDescarga]
    : soDescarga;

  const z = useJanela(useMemo(() => {
    const base: Ponto[] = pontos.map((p) => ({ x: p.x, y: p.y }));
    if (e0 != null) base.push({ x: pontos[0]?.x ?? 1, y: e0 });
    return base;
  }, [pontos, e0]), 0.08, { x: 'log' });

  const do_ = (id?: string) => pontos.find((p) => p.id === id) ?? null;

  const retaDo = (t: Trecho) => {
    const a = do_(selecao[t]?.a);
    const b = do_(selecao[t]?.b);
    return a && b && a.id !== b.id ? { a, b } : null;
  };

  const virgem = retaDo('compressao');

  /**
   * A construção de Pacheco Silva, quando o trecho virgem já existe.
   *
   * A reta virgem é escrita em (log σ′, e), que é o plano em que ela é
   * reta — fazer a conta em σ′ linear daria uma curva e um σ′p errado.
   */
  const pacheco = useMemo(() => {
    if (!virgem || e0 == null || carga.length < 3) return null;
    const lx = (v: number) => Math.log10(v);
    const x1 = lx(virgem.a.x);
    const x2 = lx(virgem.b.x);
    if (x1 === x2) return null;
    const m = (virgem.b.y - virgem.a.y) / (x2 - x1);
    if (m === 0) return null;
    const eDaReta = (lsigma: number) => virgem.a.y + m * (lsigma - x1);
    const sigmaDaReta = (e: number) => 10 ** (x1 + (e - virgem.a.y) / m);

    // A — a horizontal de e₀ encontra o prolongamento da reta virgem.
    const A: Ponto = { x: sigmaDaReta(e0), y: e0 };

    // B — dali, vertical até a curva de laboratório.
    const curvaLog = carga.map((p) => ({ x: lx(p.x), y: p.y }));
    const xa = lx(A.x);
    let B: Ponto | null = null;
    for (let i = 0; i + 1 < curvaLog.length; i += 1) {
      const p = curvaLog[i];
      const q = curvaLog[i + 1];
      if ((xa >= p.x && xa <= q.x) || (xa <= p.x && xa >= q.x)) {
        const t = q.x === p.x ? 0 : (xa - p.x) / (q.x - p.x);
        B = { x: A.x, y: p.y + t * (q.y - p.y) };
        break;
      }
    }
    if (!B) return null;

    // C — dali, horizontal de volta à reta virgem. A abscissa é σ′p.
    const C: Ponto = { x: sigmaDaReta(B.y), y: B.y };
    if (!Number.isFinite(C.x) || C.x <= 0) return null;
    return { A, B, C, sigmaP: C.x, eDaReta };
  }, [virgem, e0, carga]);

  /* A sugestão sobe assim que a construção fecha, e volta a null quando o
     trecho virgem é desfeito — um número sugerido que sobrevive à reta que
     o produziu é pior do que nenhum. */
  useEffect(() => {
    if (mostrarPacheco) aoSugerirSigma(pacheco?.sigmaP ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacheco?.sigmaP]);

  if (pontos.length < 3) return <SemCurva faltas={faltas} />;

  return (
    <div className={`grafico ${marca}`}>
      <Zoom z={z} />
      <Plano
        janela={z.janela}
        escalaX="log"
        tituloX="Tensão vertical efetiva σ′ (kPa) — escala log"
        tituloY="Índice de vazios e"
        desabilitado={!pode}
        aoClicar={(p) => {
          const i = maisPerto(p, pontos, {
            x: { para: (v) => Math.log10(v) },
            y: { para: (v) => v },
          });
          aoClicarPonto(pontos[i].id);
        }}
      >
        {({ px }) => (
          <>
            {/*
              * A horizontal de e₀: no bloco dos índices, referência de
              * ponta a ponta; no bloco da construção, vai do eixo até
              * encontrar a reta virgem — é o primeiro traço de Pacheco
              * Silva, e um traço tem começo e fim.
              */}
            {e0 != null && (mostrarPacheco && pacheco
              ? <Segmento a={{ x: z.janela.x0, y: e0 }} b={pacheco.A}
                  px={px} classe="reta e-zero" />
              : <Reta a={{ x: z.janela.x0, y: e0 }} b={{ x: z.janela.x1, y: e0 }}
                  px={px} classe="reta e-zero" />)}
            <Serie pontos={carga} px={px} />
            {descarga.length > 0 && <Serie pontos={descarga} px={px} />}

            {/*
              * No bloco da construção só a reta virgem (Cc). «Quero que
              * apresente apenas o Cc, e não apresente o Cs e Cr, pois
              * polui a visualização» — Humberto, 04/09/2026. Cr e Cs
              * continuam no bloco 2, que é onde se traçam.
              */}
            {TRECHOS.filter((t) => !mostrarPacheco || t.chave === 'compressao')
              .map((t) => {
                const r = retaDo(t.chave);
                return r
                  ? <Reta key={t.chave} a={r.a} b={r.b} px={px}
                      classe={`reta trecho ${t.classe}`} />
                  : null;
              })}

            {/*
              * As linhas auxiliares de Pacheco Silva, como se traça no
              * papel: A (e₀ × prolongamento da reta virgem) desce até a
              * curva em B; de B, horizontal até a reta virgem em C; de
              * C, vertical até o eixo — a abscissa é σ′p.
              */}
            {mostrarPacheco && pacheco && (
              <>
                <Segmento a={pacheco.A} b={pacheco.B} px={px} classe="reta auxiliar" />
                <Segmento a={pacheco.B} b={pacheco.C} px={px} classe="reta auxiliar" />
                <Segmento a={pacheco.C} b={{ x: pacheco.C.x, y: z.janela.y0 }}
                  px={px} classe="reta auxiliar" />
                <Marco p={pacheco.A} px={px} rotulo="A" />
                <Marco p={pacheco.B} px={px} rotulo="B" />
                <Achado p={pacheco.C} px={px}
                  rotulo={`σ′p ≈ ${num(pacheco.sigmaP, 1)} kPa`} />
              </>
            )}

            {preAdensamento != null && (
              <Reta a={{ x: preAdensamento, y: z.janela.y0 }}
                b={{ x: preAdensamento, y: z.janela.y1 }}
                px={px} classe="reta gravada" />
            )}

            {/* As pontas escolhidas ficam com alça, para se enxergar qual
                ponto entrou em qual reta sem conferir na tabela. */}
            {TRECHOS.filter((t) => !mostrarPacheco || t.chave === 'compressao')
              .flatMap((t) => {
              const r = retaDo(t.chave);
              if (!r) return [];
              return [
                <Alca key={`${t.chave}a`} p={r.a} px={px} />,
                <Alca key={`${t.chave}b`} p={r.b} px={px} />,
              ];
            })}
          </>
        )}
      </Plano>

      {mostrarPacheco ? (
        <p className="sub">
          Só a reta virgem (<strong>Cc</strong>, escolhida no bloco 2) e a
          construção de Pacheco Silva: a horizontal de e₀ até o prolongamento
          da reta virgem (<i>A</i>), a vertical até a curva (<i>B</i>), a
          horizontal de volta à reta virgem (<i>C</i>) — e a vertical de{' '}
          <i>C</i> ao eixo marca σ′p. Cr e Cs ficam no bloco 2.
        </p>
      ) : (
        <p className="sub">
          Clique nos dois pontos que definem o trecho{' '}
          <strong>{TRECHOS.find((t) => t.chave === trechoAtivo)?.nome}</strong>.
          {e0 != null && ' A linha tracejada horizontal é o índice de vazios inicial e₀.'}
          {!pode && ' Você está vendo, não traçando.'}
        </p>
      )}

      {mostrarPacheco && pacheco && (
        <p className="nota">
          Pacheco Silva sobre o trecho virgem escolhido dá{' '}
          <strong>σ′p ≈ {num(pacheco.sigmaP, 1)} kPa</strong>. O valor só entra
          no laudo depois de você gravá-lo abaixo — a construção sugere, quem
          decide assina.
        </p>
      )}
    </div>
  );
}
